import React, { useEffect, useState } from 'react';
import PhotoSlot from './PhotoSlot';
import { atticRValue } from './reportData';
import './AtticInspection.css';

// initialData / onChange: the Inspection page owns this data, so going Back and forward keeps it
const AtticInspection = ({ inspection, initialData, onChange, onBack, onContinue }) => {
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorLength, setCalculatorLength] = useState('');
  const [calculatorWidth, setCalculatorWidth] = useState('');
  const [calculatorActiveField, setCalculatorActiveField] = useState('length');
  const [currentAreaId, setCurrentAreaId] = useState(null);

  const [atticAreas, setAtticAreas] = useState(() => initialData?.atticAreas || [
    {
      id: 1,
      atticSize: '',
      existingDepth: '',
      insulationMaterials: [],
    }
  ]);

  // photos per slot: depth-<areaId>, overview-<areaId>, soffit, airSealing, hatch, exhaust, mold, pest
  const [photos, setPhotos] = useState(() => initialData?.photos || {});
  const setSlotPhotos = (key, list) => setPhotos((prev) => ({ ...prev, [key]: list }));

  const [formData, setFormData] = useState(() => initialData?.formData || {
    // 1.1 Insulation depth - using first area by default
    atticSize: '',
    existingDepth: '',
    insulationMaterials: [],
    depthRulerPhoto: false,
    atticOverviewPhoto: false,

    // 1.2 Soffits & baffles
    soffitCondition: false,
    soffitBaffleState: [],
    soffit12Notes: '',

    // 1.3 Air sealing
    airSealingPhoto: false,
    airSealingIssues: [],
    airSealing13Notes: '',

    // 1.4 Attic hatch
    hatchAccessPhoto: false,

    // 1.5 Bath & washroom exhaust
    exhaustFanPhoto: false,
    bathFanTermination: '', // nothing until the agent checks — the report never assumes

    // 1.6 Health & remediation
    moldDetected: 'no',
    moldSeverityLevel: '',
    moldCoverageArea: '',
    inspectorNotes: '',

    // 1.7 Pest evidence
    pestEvidence: 'no',
    pestNotes: '',

    // 1.8 Photos overview - auto-calculated from photos captured
  });

  // keep the Inspection page's copy of this data current
  useEffect(() => {
    if (onChange) onChange({ formData, atticAreas, photos });
  }, [formData, atticAreas, photos, onChange]);

  // the calculator keypad only types digits, "." and "+" — add the parts up (e.g. "20+15.5"), no eval
  const sumOf = (expr) =>
    String(expr || '')
      .split('+')
      .reduce((total, part) => total + (parseFloat(part) || 0), 0);

  const handleToggleMultiSelect = (field, option) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(option)
        ? prev[field].filter(o => o !== option)
        : [...prev[field], option]
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openCalculator = (areaId) => {
    setCurrentAreaId(areaId);
    setCalculatorLength('');
    setCalculatorWidth('');
    setCalculatorActiveField('length');
    setShowCalculator(true);
  };

  const closeCalculator = () => {
    setShowCalculator(false);
    setCalculatorLength('');
    setCalculatorWidth('');
    setCalculatorActiveField('length');
    setCurrentAreaId(null);
  };

  const handleCalcKeyPress = (key) => {
    if (calculatorActiveField === 'length') {
      if (key === 'C') {
        setCalculatorLength('');
      } else if (key === '⌫') {
        setCalculatorLength(calculatorLength.slice(0, -1));
      } else {
        setCalculatorLength(calculatorLength + key);
      }
    } else {
      if (key === 'C') {
        setCalculatorWidth('');
      } else if (key === '⌫') {
        setCalculatorWidth(calculatorWidth.slice(0, -1));
      } else {
        setCalculatorWidth(calculatorWidth + key);
      }
    }
  };

  const calculateAndSave = () => {
    try {
      const length = sumOf(calculatorLength);
      const width = sumOf(calculatorWidth);

      if (length && width) {
        const totalArea = Math.round(length * width);

        // Update the specific area in atticAreas
        if (currentAreaId !== null) {
          handleUpdateArea(currentAreaId, 'atticSize', totalArea.toString());
        }

        closeCalculator();
      }
    } catch (error) {
      console.error('Calculator error:', error);
    }
  };

  const handleAddArea = () => {
    console.log('Adding new area. Current areas:', atticAreas);
    const newAreaId = Math.max(...atticAreas.map(a => a.id), 0) + 1;
    const newArea = {
      id: newAreaId,
      atticSize: '',
      existingDepth: '',
      insulationMaterials: [],
    };
    console.log('New area ID:', newAreaId);
    setAtticAreas([
      ...atticAreas,
      newArea
    ]);
    console.log('State updated with new area');
  };

  const handleRemoveArea = (areaId) => {
    if (atticAreas.length > 1) {
      setAtticAreas(atticAreas.filter(area => area.id !== areaId));
      // the removed area's photos go with it
      setPhotos(({ [`depth-${areaId}`]: _d, [`overview-${areaId}`]: _o, ...rest }) => rest);
    }
  };

  const handleUpdateArea = (areaId, field, value) => {
    setAtticAreas(atticAreas.map(area =>
      area.id === areaId ? { ...area, [field]: value } : area
    ));
  };

  const handleToggleMaterialArea = (areaId, material) => {
    setAtticAreas(atticAreas.map(area => {
      if (area.id === areaId) {
        return {
          ...area,
          insulationMaterials: area.insulationMaterials.includes(material)
            ? area.insulationMaterials.filter(m => m !== material)
            : [...area.insulationMaterials, material]
        };
      }
      return area;
    }));
  };

  // same maths the report uses for its R-value card (area 1, weakest material)
  const calculateRValue = () => {
    const r = atticRValue(atticAreas[0]);
    return { estRValue: r.estR.toFixed(1), gapToR60: r.gapToR60.toFixed(1), depthNeeded: r.depthNeeded };
  };

  const { estRValue, gapToR60, depthNeeded } = calculateRValue();

  // photos taken so far for a slot key, or every slot starting with it (depth-1, depth-2 …)
  const count = (key) =>
    Object.keys(photos)
      .filter((k) => (key.endsWith('-') ? k.startsWith(key) : k === key))
      .reduce((n, k) => n + photos[k].length, 0);

  return (
    <div className="attic-inspection">
      {/* CONTENT */}
      <div className="attic-content">
        {/* 1.1 INSULATION DEPTH - Multiple Areas */}
        {atticAreas.map((area, areaIndex) => (
          <div key={area.id} className="attic-section">
            <div className="section-header">
              <span className="section-number">1.1</span>
              <h3>
                Insulation depth
                {areaIndex > 0 && ` - Area ${area.id}`}
              </h3>
              {areaIndex > 0 && (
                <button
                  className="btn-remove-area"
                  onClick={() => handleRemoveArea(area.id)}
                  title="Remove this area"
                >
                  ✕
                </button>
              )}
              <span className="section-status">●</span>
            </div>

            <div className="photo-capture-group">
              <PhotoSlot label="Depth ruler (required)" photos={photos[`depth-${area.id}`] || []} onChange={(list) => setSlotPhotos(`depth-${area.id}`, list)} />

              <PhotoSlot label="Attic overview (required)" photos={photos[`overview-${area.id}`] || []} onChange={(list) => setSlotPhotos(`overview-${area.id}`, list)} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Attic size</label>
                <div className="input-with-button">
                  <button className="btn-calc" onClick={() => openCalculator(area.id)}>⚡ Calc</button>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={area.atticSize}
                    onChange={(e) => handleUpdateArea(area.id, 'atticSize', e.target.value)}
                    placeholder="0 sqft"
                    className="form-input-small"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Existing depth</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={area.existingDepth}
                  onChange={(e) => handleUpdateArea(area.id, 'existingDepth', e.target.value)}
                  placeholder="0 in"
                  className="form-input-small"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label-small">INSULATION MATERIAL</label>
              <p className="form-help-text">Tap multiple if the attic has mixed materials — R/inch averages.</p>
              <div className="material-buttons">
                {['cellulose', 'fiberglass', 'fiberglass batts', 'other'].map((material) => (
                  <button
                    key={material}
                    className={`material-btn ${area.insulationMaterials.includes(material) ? 'selected' : ''}`}
                    onClick={() => handleToggleMaterialArea(area.id, material)}
                  >
                    {(material.charAt(0).toUpperCase() + material.slice(1)).replace('iber', 'ibre')}
                  </button>
                ))}
              </div>
            </div>

            {areaIndex === 0 && (
              <>
                <button className="btn-add-area" onClick={handleAddArea}>
                  + Add additional attic area
                </button>

                <div className="calculations-box">
                  <div className="calc-label">CALCULATIONS</div>
                  <div className="calc-row">
                    <div className="calc-item">
                      <div className="calc-label-small">EST. R-VALUE</div>
                      <div className="calc-value">{estRValue}</div>
                    </div>
                    <div className="calc-item">
                      <div className="calc-label-small">GAP TO R-60</div>
                      <div className="calc-value calc-highlight">{gapToR60}</div>
                    </div>
                    <div className="calc-item">
                      <div className="calc-label-small">DEPTH NEEDED</div>
                      <div className="calc-value">{depthNeeded}"</div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}

        {/* 1.2 SOFFITS & BAFFLES */}
        <div className="attic-section">
          <div className="section-header">
            <span className="section-number">1.2</span>
            <h3>Soffits & baffles</h3>
            <span className="section-status">●</span>
          </div>

          <div className="photo-capture-group">
            <PhotoSlot label="Soffit / eave condition (required)" photos={photos['soffit'] || []} onChange={(list) => setSlotPhotos('soffit', list)} />
          </div>

          <div className="form-group">
            <label className="form-label-small">SOFFIT & BAFFLE STATE</label>
            <p className="form-help-text">Select all that apply.</p>
            <div className="multi-select-buttons">
              {['Soffits blocked', 'Baffles present', 'Baffles needed'].map((option) => (
                <button
                  key={option}
                  className={`multi-btn ${formData.soffitBaffleState.includes(option) ? 'selected' : ''}`}
                  onClick={() => handleToggleMultiSelect('soffitBaffleState', option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label-small">NOTES</label>
            <textarea
              name="soffit12Notes"
              value={formData.soffit12Notes}
              onChange={handleInputChange}
              placeholder="Context for the quote (optional)..."
              className="form-textarea"
              rows="3"
            />
          </div>
        </div>

        {/* 1.3 AIR SEALING */}
        <div className="attic-section">
          <div className="section-header">
            <span className="section-number">1.3</span>
            <h3>Air sealing</h3>
            <span className="section-status">●</span>
          </div>

          <div className="photo-capture-group">
            <PhotoSlot label="Air sealing — general (required)" photos={photos['airSealing'] || []} onChange={(list) => setSlotPhotos('airSealing', list)} />
          </div>

          <div className="form-group">
            <label className="form-label-small">AIR SEALING ISSUES</label>
            <p className="form-help-text">Select all that apply.</p>
            <div className="multi-select-buttons">
              {['Gaps around penetrations', 'Pot light covers needed'].map((option) => (
                <button
                  key={option}
                  className={`multi-btn ${formData.airSealingIssues.includes(option) ? 'selected' : ''}`}
                  onClick={() => handleToggleMultiSelect('airSealingIssues', option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label-small">NOTES</label>
            <textarea
              name="airSealing13Notes"
              value={formData.airSealing13Notes}
              onChange={handleInputChange}
              placeholder="Context for the quote (optional)..."
              className="form-textarea"
              rows="3"
            />
          </div>
        </div>

        {/* 1.4 ATTIC HATCH */}
        <div className="attic-section">
          <div className="section-header">
            <span className="section-number">1.4</span>
            <h3>Attic hatch</h3>
            <span className="section-status">●</span>
          </div>

          <div className="photo-capture-group">
            <PhotoSlot label="Hatch / access (required)" photos={photos['hatch'] || []} onChange={(list) => setSlotPhotos('hatch', list)} />
          </div>
        </div>

        {/* 1.5 BATH & WASHROOM EXHAUST */}
        <div className="attic-section">
          <div className="section-header">
            <span className="section-number">1.5</span>
            <h3>Bath & washroom exhaust</h3>
            <span className="section-status">●</span>
          </div>

          <div className="photo-capture-group">
            <PhotoSlot label="Exhaust fan termination (required)" photos={photos['exhaust'] || []} onChange={(list) => setSlotPhotos('exhaust', list)} />
          </div>

          <div className="form-group">
            <label className="form-label-small">WHERE DO WASHROOM / BATH FANS TERMINATE?</label>
            <div className="radio-buttons">
              {[
                { value: 'exterior', label: 'Vented to exterior' },
                { value: 'attic', label: 'Venting into attic' },
                { value: 'confirm', label: 'Could not confirm' }
              ].map((option) => (
                <button
                  key={option.value}
                  className={`radio-btn ${formData.bathFanTermination === option.value ? 'selected' : ''}`}
                  onClick={() => handleInputChange({ target: { name: 'bathFanTermination', value: option.value } })}
                >
                  <span className="radio-indicator"></span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 1.6 HEALTH & REMEDIATION */}
        <div className="attic-section">
          <div className="section-header">
            <span className="section-number">1.6</span>
            <h3>Health & remediation status</h3>
            <span className="section-status">●</span>
          </div>

          <div className="form-group">
            <label>Signs of mould detected?</label>
            <div className="yes-no-buttons">
              <button
                className={`yes-no-btn ${formData.moldDetected === 'yes' ? 'selected' : ''}`}
                onClick={() => handleInputChange({ target: { name: 'moldDetected', value: 'yes' } })}
              >
                Yes
              </button>
              <button
                className={`yes-no-btn ${formData.moldDetected === 'no' ? 'selected' : ''}`}
                onClick={() => handleInputChange({ target: { name: 'moldDetected', value: 'no' } })}
              >
                No
              </button>
            </div>
            <p className="form-help-text">Visual staining, musty smell, soft sheathing.</p>
          </div>

          {/* FOLLOW-UP BOX - Only shows if mold detected */}
          {formData.moldDetected === 'yes' && (
            <div className="mold-followup-box">
              <div className="followup-header">⚡ FOLLOW-UP</div>

              <div className="form-group">
                <label className="form-label-small">MOULD SEVERITY</label>
                <p className="form-help-text">L1 = surface staining, spot-treatable. L2 = widespread / structural.</p>
                <div className="severity-buttons">
                  <button
                    className={`severity-btn ${formData.moldSeverityLevel === 'L1' ? 'selected' : ''}`}
                    onClick={() => handleInputChange({ target: { name: 'moldSeverityLevel', value: 'L1' } })}
                  >
                    Level 1
                  </button>
                  <button
                    className={`severity-btn ${formData.moldSeverityLevel === 'L2' ? 'selected' : ''}`}
                    onClick={() => handleInputChange({ target: { name: 'moldSeverityLevel', value: 'L2' } })}
                  >
                    Level 2
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label-small">COVERAGE AREA</label>
                <p className="form-help-text">Approximate share of the attic surface affected.</p>
                <div className="coverage-buttons">
                  {['25%', '50%', '75%', '100%'].map((percentage) => (
                    <button
                      key={percentage}
                      className={`coverage-btn ${formData.moldCoverageArea === percentage ? 'selected' : ''}`}
                      onClick={() => handleInputChange({ target: { name: 'moldCoverageArea', value: percentage } })}
                    >
                      {percentage}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label-small">MOULD EVIDENCE (required)</label>
                <div className="photo-capture-group">
                  <PhotoSlot photos={photos['mold'] || []} onChange={(list) => setSlotPhotos('mold', list)} />
                </div>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label-small">INSPECTOR NOTES</label>
            <textarea
              name="inspectorNotes"
              value={formData.inspectorNotes}
              onChange={handleInputChange}
              placeholder="Anything worth flagging."
              className="form-textarea"
              rows="3"
            />
          </div>
        </div>

        {/* 1.7 PEST EVIDENCE */}
        <div className="attic-section">
          <div className="section-header">
            <span className="section-number">1.7</span>
            <h3>Pest evidence</h3>
            <span className="section-status">●</span>
          </div>

          <div className="form-group">
            <label>Animal feces or pest evidence?</label>
            <div className="yes-no-buttons">
              <button
                className={`yes-no-btn ${formData.pestEvidence === 'yes' ? 'selected' : ''}`}
                onClick={() => handleInputChange({ target: { name: 'pestEvidence', value: 'yes' } })}
              >
                Yes
              </button>
              <button
                className={`yes-no-btn ${formData.pestEvidence === 'no' ? 'selected' : ''}`}
                onClick={() => handleInputChange({ target: { name: 'pestEvidence', value: 'no' } })}
              >
                No
              </button>
            </div>
          </div>

          {formData.pestEvidence === 'yes' && (
            <>
              <div className="form-group">
                <label className="form-label-small">PEST EVIDENCE (required)</label>
                <div className="photo-capture-group">
                  <PhotoSlot photos={photos['pest'] || []} onChange={(list) => setSlotPhotos('pest', list)} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label-small">PEST NOTES</label>
                <textarea
                  name="pestNotes"
                  value={formData.pestNotes}
                  onChange={handleInputChange}
                  placeholder="Description of pest evidence found..."
                  className="form-textarea"
                  rows="3"
                />
              </div>
            </>
          )}
        </div>

        {/* 1.8 PHOTOS OVERVIEW */}
        <div className="attic-section">
          <div className="section-header">
            <span className="section-number">1.8</span>
            <h3>Photos overview</h3>
            <span className="section-status">●</span>
          </div>

          <p className="section-help-text">Required shots for the attic inspection. Counts come from photos already captured in earlier sub-sections.</p>

          <div className="photo-requirements">
            {[
              { title: 'Access hatch', subtitle: 'Before + after', required: 1, current: count('hatch') },
              { title: 'Depth with ruler', subtitle: 'At least 2 corners', required: 2, current: count('depth-') },
              { title: 'Soffit / ridge', subtitle: 'Intake + exhaust', required: 2, current: count('soffit') },
              { title: 'Exhaust fan', subtitle: 'Show termination', required: 1, current: count('exhaust') },
              { title: 'Mould & moisture', subtitle: 'Close-ups + wide', required: 3, current: count('mold') },
              { title: 'Pest evidence', subtitle: 'If any signs', required: 1, current: count('pest') },
              { title: 'Wide overview', subtitle: 'From each corner', required: 3, current: count('overview-') },
            ].map((photo, idx) => (
              <div key={idx} className={`photo-requirement ${photo.current >= photo.required ? 'complete' : 'incomplete'}`}>
                <div className="photo-req-icon">{photo.current >= photo.required ? '✓' : '📷'}</div>
                <div className="photo-req-info">
                  <div className="photo-req-title">{photo.title}</div>
                  <div className="photo-req-subtitle">{photo.subtitle}</div>
                </div>
                <div className="photo-req-count">{photo.current} / {photo.required}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CALCULATOR MODAL */}
      {showCalculator && (
        <div className="calculator-modal-overlay" onClick={closeCalculator}>
          <div className="calculator-modal" onClick={(e) => e.stopPropagation()}>
            <div className="calculator-header">Calculate Attic Size</div>

            {/* Length Input */}
            <div
              className={`calculator-input-field ${calculatorActiveField === 'length' ? 'active' : ''}`}
              onClick={() => setCalculatorActiveField('length')}
            >
              <div className="calculator-label">Length (ft)</div>
              <div className="calculator-display">{calculatorLength || '0'}</div>
            </div>

            {/* Width Input */}
            <div
              className={`calculator-input-field ${calculatorActiveField === 'width' ? 'active' : ''}`}
              onClick={() => setCalculatorActiveField('width')}
            >
              <div className="calculator-label">Width (ft)</div>
              <div className="calculator-display">{calculatorWidth || '0'}</div>
            </div>

            {/* Total Area Display */}
            <div className="calculator-total-display">
              <div className="calculator-label">Total Area</div>
              <div className="calculator-result-display">
                {calculatorLength && calculatorWidth
                  ? (() => {
                      try {
                        const lengthResult = sumOf(calculatorLength);
                        const widthResult = sumOf(calculatorWidth);
                        return (lengthResult * widthResult).toFixed(0) + ' sq ft';
                      } catch {
                        return '0 sq ft';
                      }
                    })()
                  : '0 sq ft'}
              </div>
            </div>

            {/* Keypad */}
            <div className="calculator-keypad">
              {/* Row 1: C, ⌫, + */}
              <button className="calc-btn calc-btn-clear" onClick={() => handleCalcKeyPress('C')}>C</button>
              <button className="calc-btn calc-btn-delete" onClick={() => handleCalcKeyPress('⌫')}>⌫</button>
              <button className="calc-btn calc-btn-operator" onClick={() => handleCalcKeyPress('+')}>+</button>

              {/* Row 2: 7, 8, 9 */}
              <button className="calc-btn" onClick={() => handleCalcKeyPress('7')}>7</button>
              <button className="calc-btn" onClick={() => handleCalcKeyPress('8')}>8</button>
              <button className="calc-btn" onClick={() => handleCalcKeyPress('9')}>9</button>

              {/* Row 3: 4, 5, 6 */}
              <button className="calc-btn" onClick={() => handleCalcKeyPress('4')}>4</button>
              <button className="calc-btn" onClick={() => handleCalcKeyPress('5')}>5</button>
              <button className="calc-btn" onClick={() => handleCalcKeyPress('6')}>6</button>

              {/* Row 4: 1, 2, 3 */}
              <button className="calc-btn" onClick={() => handleCalcKeyPress('1')}>1</button>
              <button className="calc-btn" onClick={() => handleCalcKeyPress('2')}>2</button>
              <button className="calc-btn" onClick={() => handleCalcKeyPress('3')}>3</button>

              {/* Row 5: 0 (span 2), . */}
              <button className="calc-btn calc-btn-zero" onClick={() => handleCalcKeyPress('0')}>0</button>
              <button className="calc-btn" onClick={() => handleCalcKeyPress('.')}>.</button>
            </div>

            {/* Action Buttons */}
            <div className="calculator-actions">
              <button className="calculator-btn-cancel" onClick={closeCalculator}>Cancel</button>
              <button className="calculator-btn-save" onClick={calculateAndSave}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* ACTION BUTTONS */}
      <div className="attic-actions">
        <button className="btn-back" onClick={onBack}>← Back</button>
        <button className="btn-continue" onClick={() => onContinue({ formData, atticAreas, photos })}>Continue to rate →</button>
      </div>
    </div>
  );
};

export default AtticInspection;
